<template>
  <div class="foodcycle-providers">
    <!-- Provider Controls -->
    <div class="provider-controls">
      <div class="controls-wrapper">
        <button 
          class="toggle-all-btn" 
          @click="toggleAll"
          :class="{ 'active': allExpanded }"
        >
          <i :class="allExpanded ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
          {{ allExpanded ? 'Collapse All Providers' : 'Expand All Providers' }}
        </button>
        
        <div class="provider-count">
          <span class="count-badge">{{ providers.length }}</span> providers found
          <span class="hint-text">Click on the map markers to view provider details</span>
        </div>
      </div>
    </div>

    <!-- Interactive Map -->
    <CommonProviderMap
      v-if="providers && providers.length > 0"
      :providers="commonMapProviders"
      :user-lat="userLat"
      :user-lon="userLon"
      @provider-selected="handleProviderSelected"
    />

    <!-- Provider Cards List -->
    <div class="providers-list">
      <div
        v-for="(provider, index) in providers" 
        :key="index"
        class="provider-card"
        :class="{ 'expanded': collapseStates[index], 'highlighted': isHighlighted(provider) }"
        :id="`provider-card-${index}`"
      >
        <div
          class="provider-card-header"
          @click="toggleCard(index)"
        >
          <div class="provider-card-title">
            <h3 class="provider-name">{{ provider.company_name || 'Unnamed Provider' }}</h3>
            <div class="provider-meta">
              <span class="provider-category" v-if="provider.business_category">
                {{ provider.business_category }}
              </span>
              <span class="provider-distance" v-if="provider.dist && provider.dist.calculated">
                <i class="bi bi-geo-alt"></i> {{ (provider.dist.calculated / 1000).toFixed(1) }} km
              </span>
            </div>
          </div>
          
          <div class="toggle-icon"><i :class="collapseStates[index] ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
          </div>
        </div>

        <div v-if="collapseStates[index]" class="provider-card-content">
          <!-- Provider Details -->
          <div class="provider-details">
            <!-- Description -->
            <div class="detail-section" v-if="provider.description">
              <div class="section-header">
                <i class="bi bi-info-circle"></i>
                <h4 class="section-title">Description</h4>
              </div>
              <p class="description-text">{{ provider.description }}</p>
            </div>
            
            <!-- Institution Types - NEW -->
            <div class="detail-section" v-if="hasInstitutionTypes(provider)">
              <div class="section-header">
                <i class="bi bi-building"></i>
                <h4 class="section-title">Institution Types</h4>
              </div>
              <div class="tags-wrapper">
                <span
                  v-for="(type, idx) in provider.institution_types"
                  :key="`type-${idx}`"
                  class="institution-tag"
                >
                  {{ type }}
                </span>
              </div>
            </div>
            
            <!-- Capacity - NEW -->
            <div class="detail-section" v-if="hasCapacity(provider)">
              <div class="section-header">
                <i class="bi bi-box-seam"></i>
                <h4 class="section-title">Food Capacity</h4>
                <div class="section-hint">Types of food this provider needs and their capacity</div>
              </div>
              <div class="capacity-grid">
                <div 
                  v-for="(item, idx) in provider.capacity" 
                  :key="`capacity-${idx}`"
                  class="capacity-item"
                >
                  <div class="capacity-category">{{ item.food_category }}</div>
                  <div class="capacity-details">
                    <div class="capacity-status">
                      <span class="status-badge" :class="getNeededClass(item.needed)">
                        {{ item.needed }}
                      </span>
                    </div>
                    <div class="capacity-info" v-if="item.max_kg_per_week">
                      <span class="capacity-label">Maximum:</span>
                      <span class="capacity-value">{{ item.max_kg_per_week }} kg/week</span>
                    </div>
                    <div class="capacity-info" v-if="item.note">
                      <span class="capacity-label">Note:</span>
                      <span class="capacity-value">{{ item.note }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Logistics - NEW -->
            <div class="detail-section" v-if="hasLogistics(provider)">
              <div class="section-header">
                <i class="bi bi-truck"></i>
                <h4 class="section-title">Logistics</h4>
                <div class="section-hint">Pickup and delivery options</div>
              </div>
              <div class="logistics-container">
                <!-- Pickup Section -->
                <div class="logistics-section" v-if="provider.logistics.pickup">
                  <h5 class="logistics-title">
                    <i class="bi bi-box-arrow-up"></i>
                    Pickup
                  </h5>
                  <div class="logistics-details">
                    <div class="logistics-detail-item">
                      <span class="logistics-label">Available:</span>
                      <span class="logistics-value">{{ provider.logistics.pickup.available ? 'Yes' : 'No' }}</span>
                    </div>
                    <div class="logistics-detail-item" v-if="provider.logistics.pickup.radius_km">
                      <span class="logistics-label">Radius:</span>
                      <span class="logistics-value">{{ provider.logistics.pickup.radius_km }} km</span>
                    </div>
                    <div class="logistics-detail-item" v-if="provider.logistics.pickup.frequency">
                      <span class="logistics-label">Frequency:</span>
                      <span class="logistics-value">{{ provider.logistics.pickup.frequency }}</span>
                    </div>
                    <div class="logistics-detail-item">
                      <span class="logistics-label">Weekend Pickup:</span>
                      <span class="logistics-value">{{ provider.logistics.pickup.weekend ? 'Yes' : 'No' }}</span>
                    </div>
                  </div>
                </div>
                
                <!-- Delivery Section -->
                <div class="logistics-section" v-if="provider.logistics.delivery">
                  <h5 class="logistics-title">
                    <i class="bi bi-box-arrow-in-down"></i>
                    Delivery
                  </h5>
                  <div class="logistics-details">
                    <div class="logistics-detail-item">
                      <span class="logistics-label">Accepted:</span>
                      <span class="logistics-value">{{ provider.logistics.delivery.accepted ? 'Yes' : 'No' }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Company Constraints -->
            <div class="detail-section" v-if="hasConstraints(provider)">
              <div class="section-header">
                <i class="bi bi-shield-check"></i>
                <h4 class="section-title">Operational Requirements</h4>
                <div class="section-hint">
                  Any rules or conditions the provider requires for handling surplus
                </div>
              </div>
              <div class="constraints-grid">
                <div class="constraint-item" v-if="provider.company_constraints?.preparation">
                  <div class="constraint-label">Preparation</div>
                  <div class="constraint-value">{{ provider.company_constraints.preparation }}</div>
                </div>
                
                <div class="constraint-item" v-if="provider.company_constraints?.minimum_quantity_acceptance">
                  <div class="constraint-label">Minimum Quantity</div>
                  <div class="constraint-value">{{ provider.company_constraints.minimum_quantity_acceptance }}</div>
                </div>
                
                <div class="constraint-item" v-if="provider.company_constraints?.frequency">
                  <div class="constraint-label">Frequency</div>
                  <div class="constraint-value">{{ provider.company_constraints.frequency }}</div>
                </div>
                
                <div class="constraint-item" v-if="provider.company_constraints?.additional_requirements">
                  <div class="constraint-label">Additional Requirements</div>
                  <div class="constraint-value">{{ provider.company_constraints.additional_requirements }}</div>
                </div>
              </div>
            </div>
            
            <!-- Accepted Food Types -->
            <div class="detail-section" v-if="hasFoodTypes(provider)">
              <div class="section-header">
                <i class="bi bi-basket"></i>
                <h4 class="section-title">Accepted Food Types</h4>
                <div class="section-hint">
                  Food categories and items this provider can work with
                </div>
              </div>
              <div class="food-types-grid">
                <div class="food-type-category" v-if="hasFoodGroups(provider)">
                  <div class="category-label">Food Groups</div>
                  <div class="tags-wrapper">
                    <span 
                      v-for="(group, gIdx) in provider.accepted_food_types.food_group" 
                      :key="`group-${gIdx}`"
                      class="food-tag"
                    >
                      {{ group }}
                    </span>
                  </div>
                </div>
                
                <div class="food-type-category" v-if="hasFoodSubgroups(provider)">
                  <div class="category-label">Food Subgroups</div>
                  <div class="tags-wrapper">
                    <span 
                      v-for="(subgroup, sgIdx) in provider.accepted_food_types.food_subgroup" 
                      :key="`subgroup-${sgIdx}`"
                      class="food-tag"
                    >
                      {{ subgroup }}
                    </span>
                  </div>
                </div>
                
                <div class="food-type-category" v-if="hasSurplusNames(provider)">
                  <div class="category-label">Specific Surplus Items</div>
                  <div class="tags-wrapper">
                    <span 
                      v-for="(name, snIdx) in provider.accepted_food_types.surplus_name" 
                      :key="`surplus-${snIdx}`"
                      class="food-tag"
                    >
                      {{ name }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Contact Information -->
            <div class="detail-section">
              <div class="section-header">
                <i class="bi bi-person-badge"></i>
                <h4 class="section-title">Contact Information</h4>
              </div>
              <div class="contact-info">
                <div class="contact-item" v-if="provider.contact_person">
                  <div class="contact-label">Contact Person</div>
                  <div class="contact-value">{{ provider.contact_person }}</div>
                </div>
                
                <div class="contact-item" v-if="provider.contact?.email">
                  <div class="contact-label">Email</div>
                  <div class="contact-value">
                    <a :href="`mailto:${provider.contact.email}`">{{ provider.contact.email }}</a>
                  </div>
                </div>
                
                <div class="contact-item" v-if="provider.contact?.phone">
                  <div class="contact-label">Phone</div>
                  <div class="contact-value">{{ provider.contact.phone }}</div>
                </div>
                
                <div class="contact-item">
                  <div class="contact-label">Location</div>
                  <div class="contact-value">{{ formatAddress(provider.location) || 'Not specified' }}</div>
                </div>
              </div>
            </div>
            
            <!-- Additional Company Information -->
            <div class="detail-section" v-if="hasAdditionalInfo(provider)">
              <div class="section-header">
                <i class="bi bi-plus-circle"></i>
                <h4 class="section-title">Additional Information</h4>
                <div class="section-hint">
                  Additional details provided by the company
                </div>
              </div>
              <div class="additional-info">
                <div 
                  v-for="(value, key) in filteredAdditionalInfo(provider)" 
                  :key="key"
                  class="additional-item"
                >
                  <div class="additional-label">{{ formatKey(key) }}</div>
                  <div class="additional-value" v-if="key === 'website'">
                    <a :href="formatUrl(value)" target="_blank" rel="noopener">{{ value }}</a>
                  </div>
                  <div class="additional-value" v-else>
                    {{ value }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, computed } from 'vue'
import CommonProviderMap from './CommonProviderMap.vue'

export default {
  name: 'FoodcycleProviders',
  components: {
    CommonProviderMap
  },
  props: {
    providers: {
      type: Array,
      default: () => []
    },
    userLat: {
      type: Number,
      default: null
    },
    userLon: {
      type: Number,
      default: null
    }
  },
  setup(props) {
    const collapseStates = ref([]);
    const selectedMapProvider = ref(null);
    
    // Initialize collapse states for each provider (all collapsed by default)
    watch(
      () => props.providers,
      (newProviders) => {
        collapseStates.value = newProviders.map(() => false);
      },
      { immediate: true }
    );

    // Toggle collapse state for a specific provider card
    const toggleCard = (index) => {
      collapseStates.value[index] = !collapseStates.value[index];
      
      if (collapseStates.value[index]) {
        // If this card was expanded, update the selected map provider
        selectedMapProvider.value = props.providers[index];
      } else if (selectedMapProvider.value === props.providers[index]) {
        // If this card was collapsed and it was the selected map provider, clear selection
        selectedMapProvider.value = null;
      }
    };

    // Check if all providers are expanded
    const allExpanded = computed(() =>
      collapseStates.value.every((state) => state === true)
    );

    // Toggle all provider cards
    const toggleAll = () => {
      const newState = !allExpanded.value;
      collapseStates.value = collapseStates.value.map(() => newState);
      
      // Clear selected map provider if collapsing all
      if (!newState) {
        selectedMapProvider.value = null;
      }
    };

    // Check if a provider is the currently selected one from the map
    const isHighlighted = (provider) => {
      if (!selectedMapProvider.value) return false;
      return provider.company_name === selectedMapProvider.value.company_name;
    };

    // Handle when a provider is selected from the map
    const handleProviderSelected = (provider) => {
      selectedMapProvider.value = provider;
      
      // Find and expand the corresponding provider card
      const providerIndex = props.providers.findIndex(p => 
        p.company_name === provider.company_name
      );
      
      if (providerIndex !== -1) {
        collapseStates.value[providerIndex] = true;
      }
    };

    // Format address from location object
    const formatAddress = (loc) => {
      if (!loc) return '';
      const addr = loc.address || '';
      const city = loc.city || '';
      const postal = loc.postal_code || loc.postalCode || '';
      const country = loc.country || '';
      let combined = `${addr}, ${postal} ${city}, ${country}`.replace(/\s+,/g, '');
      combined = combined.trim().replace(/\s+/g, ' ');
      return combined || '';
    };

    // Format URL to ensure it has http(s)
    function formatUrl(url) {
      if (!url) return '#';
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
      }
      return `https://${url}`;
    }

    // Format camelCase or snake_case keys to readable text
    const formatKey = (key) => {
      return key
        // Insert space before capital letters
        .replace(/([A-Z])/g, ' $1')
        // Replace underscores with spaces
        .replace(/_/g, ' ')
        // Capitalize first letter
        .replace(/^./, str => str.toUpperCase())
        // Capitalize after spaces
        .replace(/\s(.)/g, (match, group) => ` ${group.toUpperCase()}`);
    };
    
    // NEW: Helper for additional_company_information/infomation (typo in DB)
    const getAdditionalInfo = (provider) => {
      // Check both variants due to typo in the database
      if (provider.additional_company_information) {
        return provider.additional_company_information;
      }
      if (provider.additional_company_infomation) {
        return provider.additional_company_infomation;
      }
      return {};
    };
    
    // Filter additional info to exclude certain fields
    function filteredAdditionalInfo(provider) {
      const info = getAdditionalInfo(provider);
      const result = {};
      
      // Copy all properties except the excluded ones
      for (const key in info) {
        if (!['non_profit', 'umbrella_organization', 'opened_year'].includes(key)) {
          result[key] = info[key];
        }
      }
      
      return result;
    }
    
    // NEW: Helper method to check if there's additional info
    const hasAdditionalInfo = (provider) => {
      const filteredInfo = filteredAdditionalInfo(provider);
      return filteredInfo && Object.keys(filteredInfo).length > 0;
    };

    // Check helper functions for conditional rendering
    const hasConstraints = (provider) => {
      return provider.company_constraints && (
        provider.company_constraints.preparation ||
        provider.company_constraints.minimum_quantity_acceptance ||
        provider.company_constraints.frequency ||
        provider.company_constraints.additional_requirements
      );
    };

    const hasFoodTypes = (provider) => {
      return provider.accepted_food_types && (
        hasFoodGroups(provider) ||
        hasFoodSubgroups(provider) ||
        hasSurplusNames(provider)
      );
    };

    const hasFoodGroups = (provider) => {
      return provider.accepted_food_types && 
             Array.isArray(provider.accepted_food_types.food_group) && 
             provider.accepted_food_types.food_group.length > 0;
    };

    const hasFoodSubgroups = (provider) => {
      return provider.accepted_food_types && 
             Array.isArray(provider.accepted_food_types.food_subgroup) && 
             provider.accepted_food_types.food_subgroup.length > 0;
    };

    const hasSurplusNames = (provider) => {
      return provider.accepted_food_types && 
             Array.isArray(provider.accepted_food_types.surplus_name) && 
             provider.accepted_food_types.surplus_name.length > 0;
    };
    
    // NEW: Helper methods for the new fields
    const hasCapacity = (provider) => {
      return provider && Array.isArray(provider.capacity) && provider.capacity.length > 0;
    };

    const hasChallenges = (provider) => {
      return provider && Array.isArray(provider.challenges) && provider.challenges.length > 0;
    };

    const hasDigitalPlatforms = (provider) => {
      return provider && Array.isArray(provider.digital_platforms_used);
    };

    const hasDisposalMethods = (provider) => {
      return provider && Array.isArray(provider.disposal_methods) && provider.disposal_methods.length > 0;
    };

    const hasInstitutionTypes = (provider) => {
      return provider && Array.isArray(provider.institution_types) && provider.institution_types.length > 0;
    };

    const hasLogistics = (provider) => {
      return provider && provider.logistics && 
        (provider.logistics.pickup || provider.logistics.delivery);
    };

    // Helper for capacity statuses
    const getNeededClass = (needed) => {
      if (needed === 'always') return 'needed-always';
      if (needed === 'sometimes') return 'needed-sometimes';
      if (needed === 'rarely') return 'needed-rarely';
      return '';
    };

    // Transform provider data for the CommonProviderMap
    const commonMapProviders = computed(() => {
      return props.providers.map(p => {
        let lat = null;
        let lon = null;

        if (
          p.location_geo &&
          p.location_geo.type === 'Point' &&
          Array.isArray(p.location_geo.coordinates)
        ) {
          lon = p.location_geo.coordinates[0];
          lat = p.location_geo.coordinates[1];
        }

        let distKm = null;
        if (p.dist && typeof p.dist.calculated === 'number') {
          distKm = p.dist.calculated / 1000;
        }

        // Add all new fields to make them available in the CommonProviderMap
        return {
          ...p, // Copy all properties from the original provider
          company_name: p.company_name || '',
          description: p.description || '',
          location_geo: (lat !== null && lon !== null)
            ? { type: 'Point', coordinates: [lon, lat] }
            : null,
          dist_km: distKm,
          business_category: p.business_category || '',
          provider_origin: 'FoodCycle.ai',
        };
      });
    });

    return {
      collapseStates,
      selectedMapProvider,
      toggleCard,
      toggleAll,
      allExpanded,
      isHighlighted,
      handleProviderSelected,
      formatAddress,
      formatKey,
      formatUrl,
      hasConstraints,
      hasFoodTypes,
      hasFoodGroups,
      hasFoodSubgroups,
      hasSurplusNames,
      getAdditionalInfo,
      filteredAdditionalInfo,
      hasAdditionalInfo,
      commonMapProviders,
      
      // NEW helpers for new fields
      hasCapacity,
      hasChallenges, 
      hasDigitalPlatforms,
      hasDisposalMethods,
      hasInstitutionTypes,
      hasLogistics,
      getNeededClass
    };
  }
};
</script>

<style scoped>
.foodcycle-providers {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Provider Controls */
.provider-controls {
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid #eee;
}

.controls-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
}

.toggle-all-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #f2f5e7;
  border: 1px solid rgba(135, 177, 64, 0.3);
  color: #558000;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.toggle-all-btn:hover {
  background-color: rgba(135, 177, 64, 0.2);
}

.toggle-all-btn.active {
  background-color: #87b140;
  color: white;
  border-color: #87b140;
}

.provider-count {
  font-size: 0.95rem;
  color: #666;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.count-badge {
  background-color: #87b140;
  color: white;
  padding: 0.25rem 0.6rem;
  border-radius: 30px;
  font-weight: 600;
  font-size: 0.9rem;
}

.hint-text {
  font-size: 0.85rem;
  color: #888;
  font-style: italic;
}

/* Providers List */
.providers-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.provider-card {
  background-color: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
  border: 1px solid #eee;
  transition: all 0.3s ease;
}

.provider-card:hover {
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
}

.provider-card.expanded {
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.provider-card.highlighted {
  border: 2px solid #87b140;
  box-shadow: 0 0 0 3px rgba(135, 177, 64, 0.2), 0 8px 25px rgba(0, 0, 0, 0.1);
}

.provider-card-header {
  padding: 1.25rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s ease;
  background-color: #f8f9fa;
  border-bottom: 1px solid #eee;
}

.provider-card.expanded .provider-card-header {
  background-color: #f2f5e7;
  border-bottom-color: rgba(135, 177, 64, 0.2);
}

.provider-card.highlighted .provider-card-header {
  background-color: #f2f5e7;
  border-bottom-color: rgba(135, 177, 64, 0.2);
}

.provider-card-header:hover {
  background-color: #f2f5e7;
}

.provider-card-title {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.provider-name {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
}

.provider-meta {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.provider-category {
  font-size: 0.9rem;
  color: #666;
}

.provider-distance {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.9rem;
  color: #666;
}

.provider-distance i {
  color: #87b140;
}

.toggle-icon {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #666;
  background-color: rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
}

.provider-card.expanded .toggle-icon,
.provider-card.highlighted .toggle-icon {
  background-color: rgba(135, 177, 64, 0.15);
  color: #558000;
}

/* Provider Card Content */
.provider-card-content {
  padding: 0;
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.provider-details {
  display: flex;
  flex-direction: column;
}

.detail-section {
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
}

.detail-section:last-child {
  border-bottom: none;
}

.section-header {
  margin-bottom: 1.25rem;
}

.section-title {
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.section-header i {
  color: #87b140;
  font-size: 1.1rem;
}

.section-hint {
  font-size: 0.85rem;
  color: #666;
  line-height: 1.5;
}

.description-text {
  color: #555;
  line-height: 1.6;
  margin: 0;
}

/* Tags and badges general styles */
.tags-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.institution-tag {
  background-color: rgba(135, 177, 64, 0.1);
  color: #558000;
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
  font-size: 0.9rem;
  border: 1px solid rgba(135, 177, 64, 0.15);
}

/* NEW: Capacity styles */
.capacity-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
}

.capacity-item {
  background-color: #f9f9f9;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #eee;
}

.capacity-category {
  padding: 0.75rem 1rem;
  background-color: #f2f5e7;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #e0e0e0;
}

.capacity-details {
  padding: 1rem;
}

.capacity-status {
  margin-bottom: 0.75rem;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 16px;
  font-size: 0.85rem;
  font-weight: 500;
}

.needed-always {
  background-color: rgba(76, 175, 80, 0.15);
  color: #2e7d32;
}

.needed-sometimes {
  background-color: rgba(255, 193, 7, 0.15);
  color: #f57f17;
}

.needed-rarely {
  background-color: rgba(244, 67, 54, 0.15);
  color: #c62828;
}

.capacity-info {
  display: flex;
  margin-bottom: 0.5rem;
}

.capacity-info:last-child {
  margin-bottom: 0;
}

.capacity-label {
  font-weight: 600;
  color: #555;
  width: 80px;
  flex-shrink: 0;
}

.capacity-value {
  color: #333;
}

/* NEW: Logistics styles */
.logistics-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.25rem;
}

.logistics-section {
  background-color: #f9f9f9;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #eee;
}

.logistics-title {
  padding: 0.75rem 1rem;
  background-color: #f2f5e7;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #e0e0e0;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
}

.logistics-title i {
  color: #87b140;
}

.logistics-details {
  padding: 1rem;
}

.logistics-detail-item {
  margin-bottom: 0.75rem;
  display: flex;
  justify-content: space-between;
}

.logistics-detail-item:last-child {
  margin-bottom: 0;
}

.logistics-label {
  font-weight: 600;
  color: #555;
}

.logistics-value {
  color: #333;
}

/* Constraints Grid */
.constraints-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
}

.constraint-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.constraint-label {
  font-weight: 600;
  color: #555;
  font-size: 0.95rem;
}

.constraint-value {
  background-color: #f9f9f9;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  color: #444;
  font-size: 0.95rem;
  line-height: 1.5;
  border-left: 3px solid #87b140;
}

/* Food Types Grid */
.food-types-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
}

.food-type-category {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.category-label {
  font-weight: 600;
  color: #555;
  font-size: 0.95rem;
}

.food-tag {
  background-color: rgba(135, 177, 64, 0.1);
  color: #558000;
  padding: 0.35rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  border: 1px solid rgba(135, 177, 64, 0.2);
}

/* Additional Info */
.additional-info {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
}

.additional-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.additional-label {
  font-weight: 600;
  color: #555;
  font-size: 0.95rem;
}

.additional-value {
  color: #333;
  font-size: 0.95rem;
}

.additional-value a {
  color: #558000;
  text-decoration: none;
  transition: color 0.2s ease;
}

.additional-value a:hover {
  color: #87b140;
  text-decoration: underline;
}

/* Contact Info */
.contact-info {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
}

.contact-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.contact-label {
  font-weight: 600;
  color: #555;
  font-size: 0.95rem;
}

.contact-value {
  color: #333;
  font-size: 0.95rem;
}

.contact-value a {
  color: #558000;
  text-decoration: none;
  transition: color 0.2s ease;
}

.contact-value a:hover {
  color: #87b140;
  text-decoration: underline;
}

.empty-info {
  color: #888;
  font-style: italic;
  margin: 0;
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .provider-card-header {
    padding: 1rem 1.25rem;
  }
  
  .detail-section {
    padding: 1.25rem;
  }
  
  .provider-name {
    font-size: 1.1rem;
  }
  
  .constraints-grid,
  .food-types-grid,
  .contact-info,
  .additional-info,
  .capacity-grid,
  .logistics-container {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 576px) {
  .controls-wrapper {
    flex-direction: column;
    align-items: stretch;
  }
  
  .toggle-all-btn {
    width: 100%;
    justify-content: center;
  }
  
  .provider-count {
    justify-content: center;
    text-align: center;
  }
}
</style>